import React, { useState } from 'react';
import { Box, IconButton, Paper, TextField, Tooltip, Menu, MenuItem } from '@mui/material';
import CodeIcon from '@mui/icons-material/Code';
import PreviewIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';

export interface PlaceholderItem {
  token: string;
  label: string;
}

interface HtmlEditorProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  minRows?: number;
  placeholders?: PlaceholderItem[];
}

/**
 * Simple HTML editor with toggle between code editor and preview (rendered HTML).
 * Similar UX to MarkdownEditor but without markdown conversion.
 */
const HtmlEditor: React.FC<HtmlEditorProps> = ({ label, value, onChange, minRows = 6, placeholders = [] }) => {
  const [mode, setMode] = useState<'edit' | 'preview'>('preview');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const textRef = React.useRef<HTMLTextAreaElement>(null);

  const handleToggleMode = () => {
    setMode((prev) => (prev === 'edit' ? 'preview' : 'edit'));
  };

  const insertAtCursor = (snippet: string) => {
    const el = textRef.current;
    if (!el) return;
    const { selectionStart, selectionEnd, value: current } = el;
    const newValue = current.slice(0, selectionStart) + snippet + current.slice(selectionEnd);
    onChange(newValue);
    // update cursor
    setTimeout(() => {
      el.selectionStart = el.selectionEnd = selectionStart + snippet.length;
      el.focus();
    }, 0);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        {label && (
          <Box component="span" sx={{ fontWeight: 500, mr: 'auto' }}>
            {label}
          </Box>
        )}
        {mode === 'edit' && placeholders.length > 0 && (
          <Tooltip title="Insert placeholder">
            <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)}>
              <CodeIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title={mode === 'edit' ? 'Preview' : 'Edit'}>
          <IconButton size="small" onClick={handleToggleMode}>
            {mode === 'edit' ? <PreviewIcon fontSize="small" /> : <EditIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Box>

      {mode === 'edit' ? (
        <TextField
          fullWidth
          multiline
          minRows={minRows}
          inputRef={textRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          variant="outlined"
        />
      ) : (
        <Paper variant="outlined" sx={{ p: 2, minHeight: `${minRows * 24}px`, overflowX: 'auto' }}>
          {/* eslint-disable-next-line react/no-danger */}
          <div
            dangerouslySetInnerHTML={{
              __html:
                (value
                  ? value.replace(/<<([^>]+)>>/g, '&lt;&lt;$1&gt;&gt;')
                  : '<em>No content</em>'),
            }}
          />
        </Paper>
      )}

      {/* Placeholder menu */}
      {placeholders.length > 0 && (
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
        >
          {placeholders.map((p) => (
            <MenuItem
              key={p.token}
              onClick={() => {
                insertAtCursor(`<<${p.token}>>`);
                setAnchorEl(null);
              }}
            >
              <strong>{`<<${p.token}>>`}</strong>&nbsp;— {p.label}
            </MenuItem>
          ))}
        </Menu>
      )}
    </Box>
  );
};

export default HtmlEditor;
